import { useMemo, useRef, useState } from 'react'
import { ACCEPTED_FILE_EXTENSIONS, MAX_UPLOAD_BYTES } from '../lib/constants'
import {
  ExternalLinkIcon,
  FileIcon,
  LinkIcon,
  PlusIcon,
  TrashIcon,
  UploadIcon,
  XIcon,
} from './Icons'
import Modal from './Modal'

const ACCEPT_VALUE = ACCEPTED_FILE_EXTENSIONS.join(',')

function readableDate(value) {
  if (!value) return ''
  return new Intl.DateTimeFormat('en', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(value))
}

function readableMonth(value) {
  if (!value) return ''
  const [year, month] = value.slice(0, 7).split('-').map(Number)
  if (!year || !month) return value
  return new Intl.DateTimeFormat('en', {
    month: 'short',
    year: 'numeric',
  }).format(new Date(year, month - 1, 1))
}

function artifactMonths(artifact) {
  const values = Array.isArray(artifact.month_starts) && artifact.month_starts.length
    ? artifact.month_starts
    : [artifact.month_start]

  return [...new Set(values.filter(Boolean))].sort()
}

function monthStartFromInput(value) {
  return /^\d{4}-\d{2}$/.test(value) ? `${value}-01` : ''
}

function sameFile(left, right) {
  return (
    left.name === right.name &&
    left.size === right.size &&
    left.lastModified === right.lastModified
  )
}

export default function ArtifactModal({
  resource,
  monthLabel,
  monthStart,
  artifacts,
  demoMode,
  onClose,
  onUpload,
  onOpen,
  onDelete,
}) {
  const fileInputRef = useRef(null)
  const [files, setFiles] = useState([])
  const [monthInput, setMonthInput] = useState(monthStart.slice(0, 7))
  const [selectedMonths, setSelectedMonths] = useState([monthStart])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const uploadLabel = useMemo(() => {
    if (!files.length) return 'Choose weekly files'
    if (files.length === 1) return files[0].name
    return `${files.length} files selected`
  }, [files])

  function handleFilesSelected(event) {
    const selected = Array.from(event.target.files || [])
    setError('')
    setFiles((current) => {
      const merged = [...current]
      selected.forEach((file) => {
        if (!merged.some((item) => sameFile(item, file))) merged.push(file)
      })
      return merged
    })
    event.target.value = ''
  }

  function removeFile(index) {
    setFiles((current) => current.filter((_, fileIndex) => fileIndex !== index))
  }

  function addMonth() {
    const nextMonth = monthStartFromInput(monthInput)
    setError('')

    if (!nextMonth) {
      setError('Choose a valid month.')
      return
    }

    setSelectedMonths((current) => [...new Set([...current, nextMonth])].sort())
  }

  function removeMonth(value) {
    if (value === monthStart) return
    setSelectedMonths((current) => current.filter((month) => month !== value))
  }

  async function handleUpload(event) {
    event.preventDefault()
    setError('')

    if (!files.length) {
      setError('Choose at least one weekly timesheet file to upload.')
      return
    }

    const oversized = files.find((file) => file.size > MAX_UPLOAD_BYTES)
    if (oversized) {
      setError(`“${oversized.name}” is larger than 6 MB.`)
      return
    }

    setBusy(true)
    try {
      await onUpload({ files, monthStarts: selectedMonths })
      setFiles([])
      if (fileInputRef.current) fileInputRef.current.value = ''
    } catch (uploadError) {
      setError(uploadError.message || 'The files could not be uploaded.')
    } finally {
      setBusy(false)
    }
  }

  async function handleOpen(artifact) {
    setError('')
    const newWindow = window.open('about:blank', '_blank')
    if (newWindow) newWindow.opener = null

    try {
      const url = await onOpen(artifact)
      if (!url) {
        if (newWindow) newWindow.close()
        setError(
          demoMode
            ? 'Demo mode stores the file name only. Connect Supabase to upload and open the actual file.'
            : 'This attachment does not have a valid URL.',
        )
        return
      }
      if (newWindow) newWindow.location.href = url
      else window.location.assign(url)
    } catch (openError) {
      if (newWindow) newWindow.close()
      setError(openError.message || 'The attachment could not be opened.')
    }
  }

  async function handleDelete(artifact) {
    const months = artifactMonths(artifact)
    const suffix = months.length > 1 ? ` from all ${months.length} assigned months` : ''
    const confirmed = window.confirm(`Remove “${artifact.label}”${suffix}?`)
    if (!confirmed) return

    setBusy(true)
    setError('')
    try {
      await onDelete(artifact)
    } catch (deleteError) {
      setError(deleteError.message || 'The attachment could not be removed.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      className="artifact-modal"
      onClose={onClose}
      subtitle={`${resource.name} · ${monthLabel}`}
      title="Weekly timesheet files"
    >
      <div className="modal-body artifact-body">
        <section>
          <div className="section-heading">
            <div>
              <h3>Files visible in {monthLabel}</h3>
              <p>Weekly Excel files and other uploaded evidence assigned to this month.</p>
            </div>
            <span className="count-badge">{artifacts.length}</span>
          </div>

          {artifacts.length ? (
            <div className="artifact-list">
              {artifacts.map((artifact) => {
                const months = artifactMonths(artifact)
                return (
                  <div className="artifact-item" key={artifact.id}>
                    <span className={`artifact-icon artifact-icon--${artifact.kind === 'link' ? 'link' : 'file'}`}>
                      {artifact.kind === 'link' ? <LinkIcon size={17} /> : <FileIcon size={17} />}
                    </span>
                    <div className="artifact-copy">
                      <strong>{artifact.label}</strong>
                      <small>
                        {artifact.kind === 'link' ? 'Legacy approval link' : 'Uploaded file'}
                        {artifact.created_at ? ` · ${readableDate(artifact.created_at)}` : ''}
                      </small>
                      <span className="artifact-month-list">
                        {months.map((value) => (
                          <i key={value}>{readableMonth(value)}</i>
                        ))}
                      </span>
                    </div>
                    <button
                      aria-label={`Open ${artifact.label}`}
                      className="icon-button"
                      disabled={busy}
                      onClick={() => handleOpen(artifact)}
                      title="Open"
                      type="button"
                    >
                      <ExternalLinkIcon size={17} />
                    </button>
                    <button
                      aria-label={`Remove ${artifact.label}`}
                      className="icon-button icon-button--danger"
                      disabled={busy}
                      onClick={() => handleDelete(artifact)}
                      title="Remove"
                      type="button"
                    >
                      <TrashIcon size={17} />
                    </button>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="empty-artifacts">
              <FileIcon size={24} />
              <strong>No weekly files uploaded yet</strong>
              <span>Upload Excel files below. Each file will appear in every month you assign.</span>
            </div>
          )}
        </section>

        <form className="artifact-form-card artifact-form-card--wide" onSubmit={handleUpload}>
          <span className="artifact-form-icon">
            <UploadIcon />
          </span>
          <div>
            <h3>Upload weekly files</h3>
            <p>Select one or more Excel files. PDF, CSV, image, Word, .eml and .msg files are also accepted. Maximum 6 MB per file.</p>
          </div>

          <label className="file-picker">
            <input
              accept={ACCEPT_VALUE}
              multiple
              onChange={handleFilesSelected}
              ref={fileInputRef}
              type="file"
            />
            <span>{uploadLabel}</span>
          </label>

          {files.length ? (
            <div className="selected-file-list" aria-label="Selected files">
              {files.map((file, index) => (
                <div className="selected-file" key={`${file.name}-${file.size}-${file.lastModified}`}>
                  <FileIcon size={15} />
                  <span title={file.name}>{file.name}</span>
                  <small>{Math.max(file.size / 1024, 1).toFixed(0)} KB</small>
                  <button
                    aria-label={`Remove ${file.name}`}
                    disabled={busy}
                    onClick={() => removeFile(index)}
                    type="button"
                  >
                    <XIcon size={14} />
                  </button>
                </div>
              ))}
            </div>
          ) : null}

          <div className="month-assignment">
            <div className="month-assignment-heading">
              <strong>Show these files in</strong>
              <small>The current month stays selected. Add another month when a weekly file spans reporting months.</small>
            </div>

            <div className="month-chip-list">
              {selectedMonths.map((value) => (
                <span className={`month-chip ${value === monthStart ? 'month-chip--current' : ''}`} key={value}>
                  {readableMonth(value)}
                  {value === monthStart ? (
                    <small>Current</small>
                  ) : (
                    <button
                      aria-label={`Remove ${readableMonth(value)}`}
                      disabled={busy}
                      onClick={() => removeMonth(value)}
                      type="button"
                    >
                      <XIcon size={13} />
                    </button>
                  )}
                </span>
              ))}
            </div>

            <div className="month-add-row">
              <input
                aria-label="Additional month"
                disabled={busy}
                onChange={(event) => setMonthInput(event.target.value)}
                type="month"
                value={monthInput}
              />
              <button className="button button--secondary button--compact" disabled={busy} onClick={addMonth} type="button">
                <PlusIcon size={15} />
                Add month
              </button>
            </div>
          </div>

          <button className="button button--primary button--full" disabled={busy || !files.length} type="submit">
            {busy
              ? 'Uploading…'
              : `Upload ${files.length || ''} file${files.length === 1 ? '' : 's'}`.replace('Upload  files', 'Upload files')}
          </button>
        </form>

        {error ? <p className="form-error">{error}</p> : null}
        {demoMode ? (
          <p className="demo-note">
            Demo mode stores file names and month assignments in this browser; uploaded file contents are not retained.
          </p>
        ) : null}
      </div>

      <footer className="modal-footer">
        <button className="button button--secondary" disabled={busy} onClick={onClose} type="button">
          Done
        </button>
      </footer>
    </Modal>
  )
}
